// Leave headroom under the browser's 64 KiB aggregate keepalive budget.
export const MAX_LOG_BATCH_BYTES = 32 * 1024;
export const MAX_OFFLINE_BATCHES = 5;
export const MAX_OFFLINE_QUEUE_BYTES = 128 * 1024;
export const REQUEST_TIMEOUT_MS = 10 * 1000;
export const RETRY_DELAYS_MS = [250, 1000] as const;

export interface AuthenticatedLogTransportDependencies {
  readonly loggerUrl: string;
  readonly token: () => Promise<string | null>;
  readonly request: (
    input: RequestInfo,
    init?: RequestInit,
  ) => Promise<Response>;
  readonly online: () => boolean;
  readonly wait: (durationMs: number) => Promise<void>;
}

type DeliveryResult = 'delivered' | 'drop' | 'offline';

export interface AuthenticatedLogTransport {
  readonly send: (batch: string) => Promise<void>;
  readonly flushOfflineQueue: () => Promise<void>;
}

const valueToByteLength = (value: string): number =>
  new Blob([value]).size;

const statusToRetryable = (status: number): boolean =>
  status === 408 || status === 429 || status >= 500;

export const authenticatedLogTransportFor = (
  dependencies: AuthenticatedLogTransportDependencies,
): AuthenticatedLogTransport => {
  const offlineQueue: string[] = [];
  let offlineQueueBytes = 0;
  let draining = false;

  const enqueue = (batch: string, batchBytes: number): void => {
    if (batchBytes > MAX_OFFLINE_QUEUE_BYTES) {
      return;
    }

    while (
      offlineQueue.length >= MAX_OFFLINE_BATCHES
      || offlineQueueBytes + batchBytes > MAX_OFFLINE_QUEUE_BYTES
    ) {
      const droppedBatch = offlineQueue.shift();
      if (droppedBatch === undefined) {
        return;
      }
      offlineQueueBytes -= valueToByteLength(droppedBatch);
    }

    offlineQueue.push(batch);
    offlineQueueBytes += batchBytes;
  };

  const requestWithTimeout = async (
    token: string,
    batch: string,
  ): Promise<Response | null> => {
    const requestController =
      typeof AbortController === 'undefined'
        ? null
        : new AbortController();
    let requestTimeout: number | undefined;

    try {
      const requestTimeoutResult = new Promise<null>((resolve) => {
        requestTimeout = window.setTimeout(() => {
          requestController?.abort();
          resolve(null);
        }, REQUEST_TIMEOUT_MS);
      });

      return await Promise.race([
        dependencies.request(
          dependencies.loggerUrl,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: batch,
            credentials: 'omit',
            keepalive: true,
            referrerPolicy: 'no-referrer',
            ...(requestController
              ? { signal: requestController.signal }
              : {}),
          },
        ),
        requestTimeoutResult,
      ]);
    } finally {
      if (requestTimeout !== undefined) {
        window.clearTimeout(requestTimeout);
      }
    }
  };

  const deliver = async (batch: string): Promise<DeliveryResult> => {
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
      if (!dependencies.online()) {
        return 'offline';
      }

      let token: string | null;
      try {
        token = await dependencies.token();
      } catch {
        return 'drop';
      }

      if (!token) {
        return 'drop';
      }

      try {
        const response = await requestWithTimeout(token, batch);

        if (response) {
          if (response.ok) {
            return 'delivered';
          }

          if (!statusToRetryable(response.status)) {
            return 'drop';
          }
        }
      } catch {
        if (!dependencies.online()) {
          return 'offline';
        }
      }

      if (attempt < RETRY_DELAYS_MS.length) {
        await dependencies.wait(RETRY_DELAYS_MS[attempt]);
      }
    }

    return 'drop';
  };

  const send = async (batch: string): Promise<void> => {
    try {
      const batchBytes = valueToByteLength(batch);
      if (batchBytes > MAX_LOG_BATCH_BYTES) {
        return;
      }

      if (!dependencies.online()) {
        enqueue(batch, batchBytes);
        return;
      }

      const result = await deliver(batch);
      if (result === 'offline') {
        enqueue(batch, batchBytes);
      }
    } catch {
      // Logging must never interrupt the application.
      return;
    }
  };

  const flushOfflineQueue = async (): Promise<void> => {
    if (draining || !dependencies.online()) {
      return;
    }

    draining = true;
    try {
      while (offlineQueue.length > 0 && dependencies.online()) {
        const batch = offlineQueue.shift()!;
        const batchBytes = valueToByteLength(batch);
        offlineQueueBytes -= batchBytes;

        const result = await deliver(batch);
        if (result === 'offline') {
          offlineQueue.unshift(batch);
          offlineQueueBytes += batchBytes;
          return;
        }
      }
    } catch {
      // Logging must never interrupt the application.
      return;
    } finally {
      draining = false;
    }
  };

  return {
    send,
    flushOfflineQueue,
  };
};
